import { z } from 'zod';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type {
  DetachWindowBounds,
  DetachedClosedPayload,
  DetachCopilotPayload,
  DetachTabPayload,
  StoreFullSyncPayload,
  StoreSyncPayload,
  StoreSyncStoreName,
} from './detached-window-types';

export const StoreSyncStoreNameSchema = z.enum([
  'layout',
  'chatTabs',
  'chatInstances',
  'project',
]) satisfies z.ZodType<StoreSyncStoreName>;

export const StoreSyncPayloadSchema = z.object({
  storeName: StoreSyncStoreNameSchema,
  patch: z.record(z.string(), z.unknown()),
  sourceWindowId: z.string().min(1),
}) satisfies z.ZodType<StoreSyncPayload>;

export const StoreFullSyncPayloadSchema = z.object({
  layout: z.record(z.string(), z.unknown()),
  chatTabs: z.record(z.string(), z.unknown()),
  chatInstances: z.record(z.string(), z.unknown()),
  project: z.record(z.string(), z.unknown()),
  sourceWindowId: z.string().min(1),
}) satisfies z.ZodType<StoreFullSyncPayload>;

export const DetachedClosedPayloadSchema = z.object({
  kind: z.enum(['tab', 'copilot']),
  id: z.string().min(1),
}) satisfies z.ZodType<DetachedClosedPayload>;

const BoundsSchema = z.object({
  x: z.number().optional(),
  y: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
}) satisfies z.ZodType<DetachWindowBounds>;

export const DetachTabPayloadSchema = z.object({
  tabId: z.string().min(1),
  bounds: BoundsSchema.optional(),
}) satisfies z.ZodType<DetachTabPayload>;

export const DetachCopilotPayloadSchema = z.object({
  bounds: BoundsSchema.optional(),
}) satisfies z.ZodType<DetachCopilotPayload>;

export const ReattachTabPayloadSchema = z.object({
  tabId: z.string().min(1),
});

const QueuedMessageSyncEntrySchema = z.object({
  id: z.string(),
  message: z.custom<TerraUIMessage>(),
  createdAt: z.number(),
  state: z.enum(['queued', 'sending', 'editing']),
});

export const ChatInstancesSyncPatchSchema = z
  .object({
    queuedMessages: z.array(
      z.tuple([z.string(), z.array(QueuedMessageSyncEntrySchema)]),
    ),
  })
  .partial();

export function parseStoreSyncPayload(
  payload: unknown,
): StoreSyncPayload | null {
  const result = StoreSyncPayloadSchema.safeParse(payload);

  return result.success ? result.data : null;
}

export function parseStoreFullSyncPayload(
  payload: unknown,
): StoreFullSyncPayload | null {
  const result = StoreFullSyncPayloadSchema.safeParse(payload);

  return result.success ? result.data : null;
}

export function parseDetachTabPayload(
  payload: unknown,
): DetachTabPayload | null {
  const result = DetachTabPayloadSchema.safeParse(payload);

  return result.success ? result.data : null;
}

export function parseDetachCopilotPayload(
  payload: unknown,
): DetachCopilotPayload | null {
  const result = DetachCopilotPayloadSchema.safeParse(payload);

  return result.success ? result.data : null;
}

export function parseReattachTabPayload(
  payload: unknown,
): { tabId: string } | null {
  const result = ReattachTabPayloadSchema.safeParse(payload);

  return result.success ? result.data : null;
}
